/**
 * src/hooks/useInstallPrompt.ts
 * Per D-20, Plan 03-05 — engagement-gated PWA install prompt hook.
 *
 * Engagement gates:
 *   - getConsent() !== null (gate de LGPD; sem o ConsentBanner, quase ninguém novo passa)
 *   - No recent dismissal (7-day cooldown via localStorage)
 *   - beforeinstallprompt event captured (or iOS Safari UA detected → iosFallback)
 *   - scrollY >= 300px AND elapsed >= 60s
 *   - requestSlot("pwa_install") granted (orchestrator mutex — push/exitIntent take priority)
 *
 * iOS Safari: beforeinstallprompt does NOT fire on iOS.
 * Hook detects iOS via UA and sets iosFallback:true so component shows manual A2HS instructions.
 * (RESEARCH Pitfall E; T-3-28: UA spoof → wrong instructions displayed, worst case)
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { getConsent } from "@/lib/consent";
import { getActive, requestSlot, releaseSlot } from "@/lib/conversion-orchestrator";
import { trackPwaInstallPrompt } from "@/lib/analytics";

// D-20 engagement thresholds
const ENGAGEMENT_TIME_MS = 60_000;
const ENGAGEMENT_SCROLL_PX = 300;
const DISMISS_COOLDOWN_DAYS = 7;
const DISMISS_KEY = "installPromptDismissed";
const DISMISS_AT_KEY = "installPromptDismissedAt";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

/** iOS Safari detection (Pitfall E — UA-based heuristic) */
function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
}

/** Detects if already installed (standalone mode) */
function isInStandaloneMode(): boolean {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

/** Check if dismissed within cooldown window */
function isDismissedRecently(): boolean {
  if (typeof localStorage === "undefined") return false;
  if (!localStorage.getItem(DISMISS_KEY)) return false;
  const ts = Number(localStorage.getItem(DISMISS_AT_KEY) || "0");
  return Date.now() - ts < DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
}

export function useInstallPrompt() {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [canShow, setCanShow] = useState(false);
  const [iosFallback, setIosFallback] = useState(false);
  const startRef = useRef<number>(Date.now());

  // Step 1: capture beforeinstallprompt event (or detect iOS)
  useEffect(() => {
    if (isInStandaloneMode()) return;

    if (isIOS()) {
      setIosFallback(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      setEvent(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Step 2: engagement gate — runs when event or iosFallback changes
  useEffect(() => {
    if (!event && !iosFallback) return;
    if (getConsent() === null) return;
    if (isDismissedRecently()) return;

    const checkEngagement = () => {
      const elapsed = Date.now() - startRef.current;
      if (elapsed >= ENGAGEMENT_TIME_MS && window.scrollY >= ENGAGEMENT_SCROLL_PX) {
        if (getActive() !== null) return;
        if (!requestSlot("pwa_install")) return;
        trackPwaInstallPrompt("shown");
        setCanShow(true);
        window.removeEventListener("scroll", checkEngagement);
        clearInterval(intervalRef.current!);
      }
    };

    const intervalRef = { current: null as ReturnType<typeof setInterval> | null };
    window.addEventListener("scroll", checkEngagement, { passive: true });
    intervalRef.current = setInterval(checkEngagement, 5000);

    return () => {
      window.removeEventListener("scroll", checkEngagement);
      if (intervalRef.current !== null) clearInterval(intervalRef.current);
    };
  }, [event, iosFallback]);

  /** Accept: fires native prompt (non-iOS) and tracks outcome */
  const accept = useCallback(async () => {
    if (!event) {
      // iOS fallback or no event — treat as dismissed (user tapped "Instalar" but no native prompt)
      trackPwaInstallPrompt("dismissed");
      setCanShow(false);
      releaseSlot("pwa_install");
      return;
    }
    await event.prompt();
    const choice = await event.userChoice;
    trackPwaInstallPrompt(choice.outcome === "accepted" ? "accepted" : "dismissed");
    setCanShow(false);
    setEvent(null);
    releaseSlot("pwa_install");
  }, [event]);

  /** Dismiss: sets 7-day cooldown and releases slot */
  const dismiss = useCallback(() => {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(DISMISS_KEY, "true");
      localStorage.setItem(DISMISS_AT_KEY, String(Date.now()));
    }
    trackPwaInstallPrompt("dismissed");
    setCanShow(false);
    releaseSlot("pwa_install");
  }, []);

  return { canShow, iosFallback, accept, dismiss };
}
